import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, doc, getDoc, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { Container, Card, Form, Button, Spinner, Alert, Row, Col, InputGroup, Table, ProgressBar, Modal } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useOrder } from './OrderContext';
import "../App.css";

// 都道府県リスト
const PREFECTURES = [
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
  "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県",
  "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県",
  "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県",
  "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県",
  "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県"
];

// --- (ヘルパー関数) ---
const normalizeForTyping = (str) => {
  if (!str) return '';
  return str.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) =>
    String.fromCharCode(s.charCodeAt(0) - 0xFEE0)
  ).replace(/-/g, '');
};

const normalizePartNumber = (partNumberStr) => {
  if (!partNumberStr) return '';
  const halfWidthStr = partNumberStr.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) =>
    String.fromCharCode(s.charCodeAt(0) - 0xFEE0)
  );
  const noHyphenStr = halfWidthStr.replace(/-/g, '');
  return noHyphenStr.toUpperCase();
};

const toHalfWidth = (str) => {
  if (!str) return '';
  return str.replace(/[０-９]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
  });
};

const calculateWeightedLength = (str) => {
  if (!str) return 0;
  let length = 0;
  for (let i = 0; i < str.length; i++) {
    length += str.charCodeAt(i) > 255 ? 2 : 1;
  }
  return length;
};

const calculateAddressLength = (address) => {
  const fullAddress = `${address.address1 || ''}${address.address2 || ''}${address.address3 || ''}`;
  return calculateWeightedLength(fullAddress);
};

const sanitizeInvoiceInput = (str) => {
  if (!str) return '';
  return str
    .replace(/[\n\r]+/g, '')
    .replace(/[!"#$%&'*+.,;<=>?@[\\\]^_`{|}~]/g, '')
    .replace(/[！＂＃＄％＆＇＊＋，．／；＜＝＞？＠［＼］＾＿｀｛｜｝～]/g, '');
};

// --- (UI補助コンポーネント) ---
const InvoiceDetailsIndicator = ({ text }) => {
  const MAX_LENGTH = 50;
  const length = calculateWeightedLength(text);
  const variant = length > MAX_LENGTH ? 'danger' : 'success';
  return (
    <div className="mt-1">
      <ProgressBar now={length} max={MAX_LENGTH} variant={variant} />
      <small className={`d-block text-end ${variant === 'danger' ? 'text-danger' : 'text-muted'}`}>
        文字数: {length} / {MAX_LENGTH} (半角1文字、全角2文字で計算)
      </small>
    </div>
  );
};

const AddressFieldIndicator = ({ text, maxLength }) => {
  const length = calculateWeightedLength(text);
  const variant = length > maxLength ? 'danger' : 'success';
  return (
    <div className="mt-1">
      <ProgressBar now={length} max={maxLength} variant={variant} />
      <small className={`d-block text-end ${variant === 'danger' ? 'text-danger' : 'text-muted'}`}>
        文字数: {length} / {maxLength}
      </small>
    </div>
  );
};

// --- (送付先入力ブロック) ---
const ShippingAddressBlock = ({
  title, addressList, selectedId, onSelectChange,
  manualAddress, onManualChange, onManualBlur, onRemove, validationErrors, prefix,
  isManual, saveChecked, onSaveToggle, isAnonymous, onSearch,
  phoneParts, onPhoneChange, onPhoneBlur,
  prefectureWarning, postalCodeWarning
}) => {
  const checkboxId = `save-address-${prefix}`;
  const MAX_LENGTH_ADDR_2_3 = 50;

  return (
    <Card className={`mb-3 h-100 ${validationErrors[`${prefix}_section`] ? 'border-danger' : ''}`}>
      <Card.Header className="d-flex justify-content-between align-items-center">
        {title}
        {onRemove && <Button variant="outline-danger" size="sm" onClick={onRemove}>削除</Button>}
      </Card.Header>
      <Card.Body>
        {!isAnonymous && (
          <Form.Group className="mb-3">
            <Form.Label>送付先の選択</Form.Label>
            <Form.Select value={selectedId} onChange={onSelectChange}>
              <option value="manual">その他 (手入力)</option>
              {addressList.map((addr, index) => (
                <option key={`${addr.id}-${index}`} value={addr.id}>
                  {addr.shippingCompanyName ? `${addr.shippingCompanyName} / ${addr.recipientName} 様` : `${addr.recipientName} 様`}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>会社名 (任意)</Form.Label>
              <Form.Control type="text" name="shippingCompanyName" value={manualAddress.shippingCompanyName || ''} onChange={onManualChange} placeholder="例: 旭興株式会社" className="custom-placeholder" />
            </Form.Group>
          </Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>支店名 (任意)</Form.Label><Form.Control type="text" name="branchName" value={manualAddress.branchName || ''} onChange={onManualChange} placeholder="例: 東京支店" className="custom-placeholder" /></Form.Group></Col>
          <Col md={6}><Form.Group className="mb-3"><Form.Label>部署名 (任意)</Form.Label><Form.Control type="text" name="departmentName" value={manualAddress.departmentName || ''} onChange={onManualChange} placeholder="例: 営業部" className="custom-placeholder" /></Form.Group></Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>送付先名</Form.Label>
              <Form.Control type="text" name="recipientName" value={manualAddress.recipientName} onChange={onManualChange} required isInvalid={!!validationErrors[`${prefix}_recipientName`]} placeholder="例: 旭興太郎" className="custom-placeholder" />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>送付先電話番号</Form.Label>
              <Row className={`g-2 align-items-center ${validationErrors[`${prefix}_phoneNumber`] ? 'border border-danger rounded' : ''}`}>
                <Col><Form.Control type="tel" name="part1" value={phoneParts.part1} onChange={onPhoneChange} onBlur={onPhoneBlur} maxLength="5" placeholder="090" className="custom-placeholder" /></Col>
                <Col xs="auto">-</Col>
                <Col><Form.Control type="tel" name="part2" value={phoneParts.part2} onChange={onPhoneChange} onBlur={onPhoneBlur} maxLength="4" placeholder="1234" className="custom-placeholder" /></Col>
                <Col xs="auto">-</Col>
                <Col><Form.Control type="tel" name="part3" value={phoneParts.part3} onChange={onPhoneChange} onBlur={onPhoneBlur} maxLength="4" placeholder="5678" className="custom-placeholder" /></Col>
              </Row>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>送付先郵便番号</Form.Label>
              <InputGroup>
                <Form.Control type="text" name="postalCode" value={manualAddress.postalCode} onChange={onManualChange} onBlur={onManualBlur} maxLength="7" required isInvalid={!!validationErrors[`${prefix}_postalCode`] || !!postalCodeWarning} placeholder="例:5310076" className="custom-placeholder" />
                <Button variant="secondary" onClick={onSearch}>住所検索</Button>
                {postalCodeWarning && <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{postalCodeWarning}</Form.Control.Feedback>}
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>送付先住所1 (都道府県)</Form.Label>
              <Form.Control type="text" name="address1" value={manualAddress.address1} onChange={onManualChange} onBlur={onManualBlur} required isInvalid={!!validationErrors[`${prefix}_address1`] || !!prefectureWarning} placeholder="例:大阪府" className="custom-placeholder" />
              {prefectureWarning && <Form.Control.Feedback type="invalid">{prefectureWarning}</Form.Control.Feedback>}
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>送付先住所2 (市区町村以降)</Form.Label>
              <Form.Control type="text" name="address2" value={manualAddress.address2} onChange={onManualChange} onBlur={onManualBlur} required isInvalid={!!validationErrors[`${prefix}_address2`]} placeholder="例:北区大淀中1-1-30" className="custom-placeholder" maxLength="50" />
              <AddressFieldIndicator text={manualAddress.address2} maxLength={MAX_LENGTH_ADDR_2_3} />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>送付先住所3 (ビル名・マンション名)</Form.Label>
              <Form.Control type="text" name="address3" value={manualAddress.address3} onChange={onManualChange} onBlur={onManualBlur} placeholder="例:梅田スカイビル" className="custom-placeholder" maxLength="50" />
              <AddressFieldIndicator text={manualAddress.address3} maxLength={MAX_LENGTH_ADDR_2_3} />
            </Form.Group>
          </Col>
        </Row>
        {isManual && !isAnonymous && (
          <Form.Group className="mt-2">
            <Form.Check type="checkbox" id={checkboxId} label="この送付先をリストに登録する" checked={saveChecked} onChange={onSaveToggle} />
          </Form.Group>
        )}
      </Card.Body>
    </Card>
  );
};

// --- (メインコンポーネント) ---
function SampleOrder() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const {
    orderData, setOrderData,
    shippingInfo1, setShippingInfo1,
    shippingInfo2, setShippingInfo2,
    showShipping2, setShowShipping2,
    saveNewAddress1, setSaveNewAddress1,
    saveNewAddress2, setSaveNewAddress2,
    setOrderId,
  } = useOrder();

  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [allowedProducts, setAllowedProducts] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [productInput, setProductInput] = useState({ start: '', end: '', quantity: 1, remarks: '' });
  const [lockRemarks, setLockRemarks] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [showEstimateModal, setShowEstimateModal] = useState(false);
  const [deliveryEstimateContent, setDeliveryEstimateContent] = useState('');
  const [invoiceInputWarning, setInvoiceInputWarning] = useState('');
  const [productRemarkWarning, setProductRemarkWarning] = useState('');
  const [productListWarnings, setProductListWarnings] = useState({});
  const [prefectureWarnings, setPrefectureWarnings] = useState({});
  const [postalCodeWarnings, setPostalCodeWarnings] = useState({});

  const initialManualAddressState = {
    recipientName: '', postalCode: '', address1: '', address2: '', address3: '', phoneNumber: '',
    shippingCompanyName: '', shippingBranchName: '', shippingDepartmentName: ''
  };

  useEffect(() => {
    const isEditing = !!orderId;
    document.title = isEditing ? '発注内容変更 | My Order System' : 'サンプル発注 | My Order System';
    setOrderId(orderId);

    const fetchData = async () => {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) { setError('ログインしていません。'); setLoading(false); return; }

      try {
        const holidayDocRef = doc(db, 'holiday_settings', 'config');
        const deliveryEstimateDocRef = doc(db, 'system_settings', 'delivery_info');
        const [holidaySnap, deliveryEstimateSnap] = await Promise.all([getDoc(holidayDocRef), getDoc(deliveryEstimateDocRef)]);

        if (holidaySnap.exists() && holidaySnap.data().specialHolidays) {
            const today = new Date(); today.setHours(0, 0, 0, 0);
            const oneMonthLater = new Date(today); oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
            const upcomingHolidays = holidaySnap.data().specialHolidays
                .filter(h => {
                    const sd = h.startDate?.toDate ? h.startDate.toDate() : new Date(h.startDate);
                    const ed = h.endDate?.toDate ? h.endDate.toDate() : new Date(h.endDate);
                    return ed >= today && sd <= oneMonthLater;
                })
                .sort((a, b) => (a.startDate.toDate() - b.startDate.toDate()));
            setHolidays(upcomingHolidays);
        }
        setDeliveryEstimateContent(deliveryEstimateSnap.exists() ? deliveryEstimateSnap.data().content : '<p>登録なし</p>');

        let userData;
        if (user.isAnonymous) {
          userData = { isAnonymous: true, userAttribute: '一般ユーザー', userAttributeCode: '0001' };
        } else {
          const userDocSnap = await getDoc(doc(db, 'users', user.uid));
          userData = userDocSnap.data();
          const addressSnap = await getDocs(query(collection(db, 'addresses', user.uid, 'shipping_addresses'), orderBy('recipientName')));
          setShippingAddresses(addressSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }
        setCurrentUserInfo(userData);

        const productSnap = await getDocs(query(collection(db, "products"), orderBy("sequentialNumber", "asc")));
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const filtered = productSnap.docs.map(doc => doc.data()).filter(p => {
          const uCodeInt = parseInt(userData.userAttributeCode || "0001", 2);
          const pCodeInt = parseInt(p.userAttributeCode, 2);
          const canOrder = (uCodeInt & pCodeInt) !== 0;
          let inPeriod = true;
          if (p.listingStartDate) inPeriod = today >= (p.listingStartDate.toDate ? p.listingStartDate.toDate() : new Date(p.listingStartDate));
          if (p.listingEndDate && inPeriod) inPeriod = today <= (p.listingEndDate.toDate ? p.listingEndDate.toDate() : new Date(p.listingEndDate));
          return canOrder && inPeriod;
        });
        setAllowedProducts(filtered.filter((p, i, self) => i === self.findIndex(t => t.partNumber === p.partNumber)));
        const catSnap = await getDocs(collection(db, 'productCatalogs'));
        setCatalogs(catSnap.docs.map(doc => doc.data()));

        if (isEditing && !orderData.invoiceDetails) {
          const orderSnap = await getDoc(doc(db, 'orders', orderId));
          if (orderSnap.exists()) {
            const d = orderSnap.data();
            setOrderData({ orderNumber: d.orderNumber, invoiceDetails: d.invoiceDetails || '', products: d.products });
            if (d.shippingDestinations[0]) setShippingInfo1({ selectedId: 'manual', manualAddress: d.shippingDestinations[0] });
            if (d.shippingDestinations[1]) { setShippingInfo2({ selectedId: 'manual', manualAddress: d.shippingDestinations[1] }); setShowShipping2(true); }
          }
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchData();
  }, [orderId, setOrderId, setOrderData, setShippingInfo1, setShippingInfo2, setShowShipping2]);

  const handlePostalCodeSearch = useCallback(async (zipCode, setInfo) => {
    if (zipCode && zipCode.length === 7) {
      try {
        const res = await axios.get(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zipCode}`);
        if (res.data.results) {
          const r = res.data.results[0];
          setInfo(prev => ({ ...prev, manualAddress: { ...prev.manualAddress, address1: r.address1, address2: r.address2 + r.address3, address3: '' } }));
        } else { alert('見つかりませんでした。'); }
      } catch (err) { console.error(err); }
    } else { alert('7桁で入力してください。'); }
  }, []);

  // --- 入力制御ハンドラ (IME対応版) ---

  const createAddressChangeHandler = (setInfo, prefix) => (e) => {
    const { name, value } = e.target;
    setInfo(prev => ({ ...prev, manualAddress: { ...prev.manualAddress, [name]: value } }));
    if (validationErrors[name]) setValidationErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
  };

  const handleAddressBlur = (e, setInfo, prefix) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'postalCode') {
      const half = toHalfWidth(value);
      const sanitized = half.replace(/[^0-9]/g, '');
      processedValue = sanitized;
      if (half !== sanitized) setPostalCodeWarnings(prev => ({ ...prev, [prefix]: '半角数字のみ入力可能です。' }));
      else if (sanitized.length > 0 && sanitized.length !== 7) setPostalCodeWarnings(prev => ({ ...prev, [prefix]: '7桁で入力してください。' }));
      else setPostalCodeWarnings(prev => { const n = { ...prev }; delete n[prefix]; return n; });
    }
    if (name === 'address1') {
      if (processedValue && !PREFECTURES.includes(processedValue)) setPrefectureWarnings(prev => ({ ...prev, [prefix]: '都道府県名を正しく入力してください。' }));
      else setPrefectureWarnings(prev => { const n = { ...prev }; delete n[prefix]; return n; });
    }
    setInfo(prev => ({ ...prev, manualAddress: { ...prev.manualAddress, [name]: processedValue } }));
  };

  const handlePhoneChange = (e, prefix) => {
    const { name, value } = e.target;
    const setInfo = prefix === 'addr1' ? setShippingInfo1 : setShippingInfo2;
    const current = prefix === 'addr1' ? shippingInfo1 : shippingInfo2;
    const parts = (current.manualAddress?.phoneNumber || '').split('-');
    const partsMap = { part1: parts[0] || '', part2: parts[1] || '', part3: parts[2] || '' };
    partsMap[name] = value;
    setInfo(prev => ({ ...prev, manualAddress: { ...prev.manualAddress, phoneNumber: `${partsMap.part1}-${partsMap.part2}-${partsMap.part3}` } }));
  };

  const handlePhoneBlur = (e, prefix) => {
    const { name, value } = e.target;
    const setInfo = prefix === 'addr1' ? setShippingInfo1 : setShippingInfo2;
    const sanitized = toHalfWidth(value).replace(/[^0-9]/g, '');
    setInfo(prev => {
      const parts = (prev.manualAddress?.phoneNumber || '').split('-');
      const pState = [parts[0] || '', parts[1] || '', parts[2] || ''];
      pState[name === 'part1' ? 0 : name === 'part2' ? 1 : 2] = sanitized;
      return { ...prev, manualAddress: { ...prev.manualAddress, phoneNumber: pState.join('-') } };
    });
  };

  const handlePartNumberChange = (e) => {
    const { name, value } = e.target;
    setProductInput(prev => ({ ...prev, [name]: value }));
  };

  const handlePartNumberBlur = (e) => {
    const { name, value } = e.target;
    setProductInput(prev => ({ ...prev, [name]: normalizeForTyping(value).toUpperCase() }));
  };

  // --- 送り状・備考ハンドラ ---

  const handleInvoiceDetailsChange = (e) => {
    const val = sanitizeInvoiceInput(e.target.value);
    if (e.target.value !== val) setInvoiceInputWarning('使用できない記号が含まれています。');
    else setInvoiceInputWarning('');
    setOrderData(prev => ({ ...prev, invoiceDetails: val }));
  };

  const handleProductRemarkChange = (e) => {
    const val = sanitizeInvoiceInput(e.target.value);
    if (e.target.value !== val) setProductRemarkWarning('記号は使用できません。');
    else setProductRemarkWarning('');
    setProductInput(prev => ({ ...prev, remarks: val }));
  };

  // ★追加：発注リスト内の備考を編集するハンドラ
  const handleRemarkChange = (indexToUpdate, newRemark) => {
    const sanitizedValue = sanitizeInvoiceInput(newRemark);

    setProductListWarnings(prev => {
        const newWarnings = { ...prev };
        if (newRemark !== sanitizedValue) {
            newWarnings[indexToUpdate] = '使用できない記号が含まれています。';
        } else {
            delete newWarnings[indexToUpdate];
        }
        return newWarnings;
    });

    setOrderData(prev => ({
      ...prev,
      products: prev.products.map((product, index) => 
        index === indexToUpdate ? { ...product, remarks: sanitizedValue } : product
      )
    }));
  };

  // --- 商品リスト操作 ---

  const handleAddProducts = async () => {
    const { start, end, quantity, remarks } = productInput;
    if (!start || !quantity) { alert('品番と数量を入力してください。'); return; }
    const nStart = normalizePartNumber(start);
    const nEnd = end ? normalizePartNumber(end) : nStart;
    setIsAddingProduct(true);
    let items = [];
    const matchingCat = catalogs.find(c => c.seriesStartDate === nStart && c.seriesEndDate === nEnd);
    if (matchingCat) items.push({ partNumber: matchingCat.catalogName, quantity, remarks, isCatalog: true });
    else {
      const startP = allowedProducts.find(p => normalizePartNumber(p.partNumber) === nStart);
      if (nEnd && nStart !== nEnd) {
        const endP = allowedProducts.find(p => normalizePartNumber(p.partNumber) === nEnd);
        if (startP && endP && startP.sequentialNumber === endP.sequentialNumber) {
          items = allowedProducts.filter(p => p.sequentialNumber === startP.sequentialNumber && p.importOrder >= Math.min(startP.importOrder, endP.importOrder) && p.importOrder <= Math.max(startP.importOrder, endP.importOrder)).map(p => ({ ...p, quantity, remarks }));
        } else if (startP && endP) { alert("カタログを跨いでの範囲指定はできません。"); setIsAddingProduct(false); return; }
      } else if (startP) items.push({ ...startP, quantity, remarks });
    }
    if (items.length > 0) {
      setOrderData(prev => {
        const newList = [...prev.products];
        items.forEach(item => {
          const idx = newList.findIndex(p => p.partNumber === item.partNumber && p.remarks === item.remarks);
          if (idx > -1) newList[idx].quantity = Number(newList[idx].quantity) + Number(item.quantity);
          else newList.push(item);
        });
        // --- 追加：リストを品番(partNumber)で自然順ソートする ---
        newList.sort((a, b) => {
          return a.partNumber.localeCompare(b.partNumber, undefined, {
            numeric: true,
            sensitivity: 'base'
          });
        });
        return { ...prev, products: newList };
      });
    } else { alert('該当する商品が見つかりません。'); }
    setProductInput(prev => ({ ...prev, start: '', end: '', quantity: 1, remarks: lockRemarks ? prev.remarks : '' }));
    setIsAddingProduct(false);
  };

  const removeProduct = (idx) => { setOrderData(prev => ({ ...prev, products: prev.products.filter((_, i) => i !== idx) })); };

  const handleConfirmOrder = () => {
    const errs = {};
    const msgs = [];
    if (calculateWeightedLength(orderData.invoiceDetails) > 50) { errs.invoiceDetails = true; msgs.push('摘要欄の文字数オーバー'); }
    
    const check = (addr, pref) => {
      if (!addr.recipientName) errs[`${pref}_recipientName`] = true;
      if (!addr.postalCode || addr.postalCode.length !== 7) errs[`${pref}_postalCode`] = true;
      if (!PREFECTURES.includes(addr.address1)) errs[`${pref}_address1`] = true;
      if (!addr.address2) errs[`${pref}_address2`] = true;
      if (calculateAddressLength(addr) > 150) msgs.push(`送付先${pref === 'addr1' ? '1' : '2'}の住所が長すぎます`);
      if (!addr.phoneNumber || addr.phoneNumber === '--' || addr.phoneNumber.replace(/-/g, '').length === 0) {
        errs[`${pref}_phoneNumber`] = true;
      }
    };
    
    check(shippingInfo1.manualAddress, 'addr1');
    if (showShipping2) check(shippingInfo2.manualAddress, 'addr2');
    if (orderData.products.length === 0) msgs.push('商品を追加してください');
    
    // 【修正】重複していたif文を1つに統合しました
    if (Object.keys(errs).length > 0 || msgs.length > 0) { 
      setValidationErrors(errs); 
      // 電話番号エラーがある場合はメッセージを先頭に追加
      if (errs.addr1_phoneNumber || errs.addr2_phoneNumber) {
        msgs.unshift('送付先電話番号を入力してください');
      }
      setError(msgs.join(' / ')); 
      return; 
    }
    
    navigate('/order-confirmation', { state: { currentUserInfo, addressCount: shippingAddresses.length } });
  };

  // --- ヘルパーレンダリング ---
  const QuantityControl = ({ quantity, onQuantityChange }) => (
    <InputGroup size="sm" style={{ width: '120px' }}>
      <Button variant="outline-secondary" onClick={() => onQuantityChange(Math.max(1, Number(quantity) - 1))}>-</Button>
      <Form.Control type="text" className="text-center" value={quantity} readOnly />
      <Button variant="outline-secondary" onClick={() => onQuantityChange(Number(quantity) + 1)}>+</Button>
    </InputGroup>
  );

  const getPhoneParts = (p) => { const s = (p || '').split('-'); return { part1: s[0] || '', part2: s[1] || '', part3: s[2] || '' }; };

  if (loading) return <Container className="d-flex justify-content-center align-items-center vh-100"><Spinner animation="border" /></Container>;

  return (
    <main>
      <Container className="my-4">
        {holidays.length > 0 && (
          <Alert variant="light" className='border'>
            <Alert.Heading as="h6">休業日のご案内</Alert.Heading>
            <ul className="mb-0 ps-3">{holidays.map(h => (<li key={h.id}><strong>{h.title}:</strong> {h.startDate.toDate().toLocaleDateString()} ~ {h.endDate.toDate().toLocaleDateString()}</li>))}</ul>
          </Alert>
        )}
        <Card className="shadow-sm">
          <Card.Header className="p-3 d-flex justify-content-between align-items-end">
            <h2 className="mb-0">{orderId ? '発注内容変更' : 'サンプル発注'}</h2>
            <Link to="/mainmenu"><small>メインメニューに戻る</small></Link>
          </Card.Header>
          <Card.Body className="p-4">
            {error && <Alert variant="danger" style={{ whiteSpace: 'pre-wrap' }}>{error}</Alert>}
            <Form>
              <Row>
                <Col lg={6}>
                  <ShippingAddressBlock title="送付先1" addressList={shippingAddresses} selectedId={shippingInfo1.selectedId} onSelectChange={(e) => {
                    const id = e.target.value; const addr = id === 'manual' ? initialManualAddressState : shippingAddresses.find(a => a.id === id);
                    setShippingInfo1({ selectedId: id, manualAddress: addr });
                  }} manualAddress={shippingInfo1.manualAddress} onManualChange={createAddressChangeHandler(setShippingInfo1, 'addr1')} onManualBlur={(e) => handleAddressBlur(e, setShippingInfo1, 'addr1')} validationErrors={validationErrors} prefix="addr1" isManual={shippingInfo1.selectedId === 'manual'} saveChecked={saveNewAddress1} onSaveToggle={(e) => setSaveNewAddress1(e.target.checked)} isAnonymous={currentUserInfo?.isAnonymous} onSearch={() => handlePostalCodeSearch(shippingInfo1.manualAddress.postalCode, setShippingInfo1)} phoneParts={getPhoneParts(shippingInfo1.manualAddress.phoneNumber)} onPhoneChange={(e) => handlePhoneChange(e, 'addr1')} onPhoneBlur={(e) => handlePhoneBlur(e, 'addr1')} prefectureWarning={prefectureWarnings['addr1']} postalCodeWarning={postalCodeWarnings['addr1']} />
                </Col>
                <Col lg={6}>
                  {showShipping2 ? (
                    <ShippingAddressBlock title="送付先2" addressList={shippingAddresses} selectedId={shippingInfo2.selectedId} onSelectChange={(e) => {
                      const id = e.target.value; const addr = id === 'manual' ? initialManualAddressState : shippingAddresses.find(a => a.id === id);
                      setShippingInfo2({ selectedId: id, manualAddress: addr });
                    }} manualAddress={shippingInfo2.manualAddress} onManualChange={createAddressChangeHandler(setShippingInfo2, 'addr2')} onManualBlur={(e) => handleAddressBlur(e, setShippingInfo2, 'addr2')} onRemove={() => setShowShipping2(false)} validationErrors={validationErrors} prefix="addr2" isManual={shippingInfo2.selectedId === 'manual'} saveChecked={saveNewAddress2} onSaveToggle={(e) => setSaveNewAddress2(e.target.checked)} isAnonymous={currentUserInfo?.isAnonymous} onSearch={() => handlePostalCodeSearch(shippingInfo2.manualAddress.postalCode, setShippingInfo2)} phoneParts={getPhoneParts(shippingInfo2.manualAddress.phoneNumber)} onPhoneChange={(e) => handlePhoneChange(e, 'addr2')} onPhoneBlur={(e) => handlePhoneBlur(e, 'addr2')} prefectureWarning={prefectureWarnings['addr2']} postalCodeWarning={postalCodeWarnings['addr2']} />
                  ) : (
                    <div className="d-flex flex-column align-items-center justify-content-center h-100 border-2 border-dashed rounded" style={{ borderStyle: 'dashed', padding: '1rem', minHeight: '300px' }}>
                      <Button variant="outline-secondary" onClick={() => setShowShipping2(true)}>+ 送付先を追加</Button>
                      <small className="mt-2 text-muted">同一内容を2箇所まで同時に発注できます。</small>
                    </div>
                  )}
                </Col>
              </Row>
              <hr className='my-4' />
              <Form.Group className="mb-3">
                <Form.Label>送り状記載事項 <small className="text-danger">(改行不可・記号制限あり)</small></Form.Label>
                <Form.Control as="textarea" rows={1} value={orderData.invoiceDetails || ''} onChange={handleInvoiceDetailsChange} isInvalid={!!invoiceInputWarning} />
                {invoiceInputWarning && <div className="text-danger small mt-1">{invoiceInputWarning}</div>}
                <InvoiceDetailsIndicator text={orderData.invoiceDetails || ''} />
              </Form.Group>
              <hr />
              <h5>商品追加</h5>
              <Card className="mb-3 bg-light"><Card.Body>
                <Row className="g-2">
                  <Col md><Form.Label>品番 (開始)</Form.Label><Form.Control type="text" name="start" value={productInput.start} onChange={handlePartNumberChange} onBlur={handlePartNumberBlur} placeholder="例: NR9081" /></Col>
                  <Col md><Form.Label>品番 (終了)</Form.Label><Form.Control type="text" name="end" value={productInput.end} onChange={handlePartNumberChange} onBlur={handlePartNumberBlur} placeholder="例: NR9087" /></Col>
                  <Col md="auto"><Form.Label>数量</Form.Label><Form.Control type="number" min="1" value={productInput.quantity} onChange={(e) => setProductInput(p => ({ ...p, quantity: Number(e.target.value) }))} style={{ width: '80px' }} /></Col>
                </Row>
                <Row className="mt-2"><Col><Form.Label>備考</Form.Label><InputGroup>
                  <Form.Control type="text" value={productInput.remarks} onChange={handleProductRemarkChange} isInvalid={!!productRemarkWarning} />
                  <Form.Check type="switch" label="備考を固定" checked={lockRemarks} onChange={(e) => setLockRemarks(e.target.checked)} className="ms-2 align-self-center" />
                </InputGroup></Col></Row>
                <div className="d-grid mt-3"><Button onClick={handleAddProducts} disabled={isAddingProduct}>{isAddingProduct ? <Spinner size="sm" /> : "リストに追加"}</Button></div>
              </Card.Body></Card>
              <h5>発注商品リスト</h5>
              <Table striped bordered hover responsive>
                <thead><tr><th>品番</th><th>数量</th><th>備考</th><th>操作</th></tr></thead>
                <tbody>{orderData.products.map((p, i) => (
                  <tr key={i}>
                    <td>{p.partNumber}</td>
                    <td>
                      <QuantityControl quantity={p.quantity} onQuantityChange={(q) => { const nl = [...orderData.products]; nl[i].quantity = q; setOrderData({ ...orderData, products: nl }); }} />
                    </td>
                    <td>
                      {/* ★編集可能なForm.Controlに修正 */}
                      <Form.Control
                        type="text"
                        size="sm"
                        value={p.remarks}
                        onChange={(e) => handleRemarkChange(i, e.target.value)}
                        isInvalid={!!productListWarnings[i]}
                      />
                      {productListWarnings[i] && (
                        <div className="text-danger small mt-1" style={{ fontSize: '0.75rem' }}>
                          <strong>!</strong> {productListWarnings[i]}
                        </div>
                      )}
                    </td>
                    <td>
                      <Button variant="danger" size="sm" onClick={() => removeProduct(i)}>削除</Button>
                    </td>
                  </tr>
                ))}</tbody>
              </Table>
              <div className="d-grid mt-4"><Button variant="primary" size="lg" onClick={handleConfirmOrder}>発注内容を確認</Button></div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default SampleOrder;